import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { CurrencyInput } from '@client/components/_ui/data-entry/CurrencyInput';
import { StatTile } from '@client/components/_ui/display/StatTile';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { notification } from '@client/components/_ui/feedback/notification';
import { Space } from '@client/components/_ui/layout/Space';
import { StripePayment } from '@client/components/financials/payments/StripePayment';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { WALLET_CURRENCY, WALLET_MINIMUM_TOP_UP } from '@shared/constants/business/wallet';
import { formatWalletAmount } from '@shared/utils/core/formatting';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Alert, Card, Modal, Typography } from 'antd';
import type { Decimal } from 'decimal.js';
import { useEffect, useState } from 'react';
import { z } from 'zod';

export const Route = createFileRoute('/console/$orgId/configuration/financials/billing/balance')({
    validateSearch: zodValidator(
        z.object({
            paymentSuccess: z.string().optional(),
        }),
    ),
    component: Balance,
});

function calculateGST(baseAmount: Decimal) {
    return baseAmount.mul(0.1);
}

function calculateTotal(baseAmount: Decimal) {
    return baseAmount.add(calculateGST(baseAmount));
}

function Balance() {
    const { paymentSuccess } = Route.useSearch();
    const { trpc } = useTrpc();
    const [amount, setAmount] = useState<Decimal | null | undefined>(null);
    const [paymentClientSecret, setPaymentClientSecret] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const utils = trpc.useUtils();

    useEffect(() => {
        if (paymentSuccess) {
            message.success('Payment successful');
            void utils.billing.getWalletBalance.invalidate();
        }
    }, [paymentSuccess]);

    const { data: balance = 0, isLoading: isBalanceLoading } = trpc.billing.getWalletBalance.useQuery();
    const { data: billingInfo } = trpc.billing.getBillingInfo.useQuery();
    const createPaymentIntentMutation = trpc.billing.createPaymentIntent.useMutation();

    async function handleAddFunds() {
        if (!amount) {
            errorMessage.show('Amount is required');
            return;
        }

        if (amount.lt(WALLET_MINIMUM_TOP_UP)) {
            errorMessage.show(`Minimum top-up amount is ${formatWalletAmount(WALLET_MINIMUM_TOP_UP)}`);
            return;
        }

        if (!billingInfo) {
            notification.error({
                title: 'Please complete billing information first.',
            });
            return;
        }

        try {
            setLoading(true);
            const result = await createPaymentIntentMutation.mutateAsync({
                amount: +amount.mul(100).toString().split('.')[0], // Convert to cents
            });
            setPaymentClientSecret(result.clientSecret);
        } catch (error) {
            errorMessage.show(error);
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <div className="flex justify-center">
                <Card title="Wallet balance" loading={isBalanceLoading} className="w-full max-w-xl">
                    <Space orientation="vertical" size="large" className="w-full">
                        <div>
                            <Alert
                                title="About your wallet"
                                description="Your wallet balance is used to pay for services and features within our platform. Funds can be added at any time and are immediately available for use."
                                type="info"
                                showIcon
                                className="mb-4"
                            />
                            <StatTile
                                label="Current balance"
                                value={formatWalletAmount(balance)}
                                description="(Available for immediate use)"
                                surface="muted"
                            />
                        </div>
                        <div className="border-t pt-4">
                            <Typography.Title level={5}>Add funds to your wallet</Typography.Title>
                            <Typography.Paragraph type="secondary" className="mb-4">
                                Top up your wallet balance to ensure uninterrupted access to our services. We accept all
                                major credit cards through our secure payment processor, Stripe. Minimum deposit amount
                                is {formatWalletAmount(WALLET_MINIMUM_TOP_UP)} (excluding GST).
                            </Typography.Paragraph>
                            <Space orientation="vertical" size="middle">
                                <Space>
                                    <CurrencyInput value={amount} onChange={setAmount} addonBefore={WALLET_CURRENCY} />
                                    <Button
                                        type="primary"
                                        onClick={handleAddFunds}
                                        disabled={!amount || amount.lt(WALLET_MINIMUM_TOP_UP)}
                                        loading={loading}
                                    >
                                        Add funds
                                    </Button>
                                </Space>
                                {amount?.gt(0) && (
                                    <Card size="small" className="w-2xs bg-muted">
                                        <Space orientation="vertical" className="w-full">
                                            <div className="flex justify-between">
                                                <Typography.Text>Base amount:</Typography.Text>
                                                <Typography.Text>{formatWalletAmount(amount)}</Typography.Text>
                                            </div>
                                            <div className="flex justify-between">
                                                <Typography.Text>GST (10%):</Typography.Text>
                                                <Typography.Text>
                                                    {formatWalletAmount(calculateGST(amount))}
                                                </Typography.Text>
                                            </div>
                                            <div className="mt-2 flex justify-between border-t pt-2">
                                                <Typography.Text strong>Total amount:</Typography.Text>
                                                <Typography.Text strong>
                                                    {formatWalletAmount(calculateTotal(amount))}
                                                </Typography.Text>
                                            </div>
                                        </Space>
                                    </Card>
                                )}
                                {amount?.gt(0) && amount?.lt(WALLET_MINIMUM_TOP_UP) && (
                                    <Typography.Text type="danger">
                                        Minimum deposit amount is {formatWalletAmount(WALLET_MINIMUM_TOP_UP)} (excluding
                                        GST)
                                    </Typography.Text>
                                )}
                            </Space>
                        </div>
                    </Space>
                </Card>
            </div>

            <Modal
                title="Add funds to wallet"
                open={!!paymentClientSecret}
                onCancel={() => setPaymentClientSecret(null)}
                footer={null}
                width="fit-content"
            >
                {paymentClientSecret && amount && (
                    <div>
                        <Typography.Paragraph className="mb-4">
                            {/* The PaymentIntent charges exactly the entered amount (createPaymentIntent). */}
                            Please enter your payment details below. Your card will be charged{' '}
                            {formatWalletAmount(amount)}.
                        </Typography.Paragraph>
                        <StripePayment clientSecret={paymentClientSecret} />
                    </div>
                )}
            </Modal>
        </>
    );
}
